import {UseFormReturnType} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Divider, Flex, Modal, ModalProps, TextInput} from "@mantine/core";
import React, {useCallback} from "react";
import {useObjectComboMappingForm} from "./useObjectComboMappingForm";
import {EntityIdentifier} from "@ots-frontend/ots-data";
import {EntityTypeSelect} from "../../select/entity/EntityTypeSelect/EntityTypeSelect";
import {EntitySelect} from "../../select/entity/EntitySelect/EntitySelect";

export interface ObjectComboMappingFormProps {
  options?: ObjectComboMappingFormOptions;
  defaultValues?: ObjectComboMappingFormDefaultValues;
  onSubmit?: (values: ObjectComboMappingFormValues, newAfter: boolean) => void;
  submitting?: boolean;
}

export interface ObjectComboMappingFormOptions {
  regionId?: string;
  newAfter?: boolean;
  leftObjectSelectDisabled?: boolean
  rightObjectSelectDisabled?: boolean
}

export interface ObjectComboMappingFormValues {
  key: string
  value: string
  leftObject: EntityIdentifier
  rightObject: EntityIdentifier
}

export interface ObjectComboMappingFormDefaultValues extends Partial<Omit<ObjectComboMappingFormValues, 'leftObject' | 'rightObject'>> {
  leftObject?: Partial<EntityIdentifier>
  rightObject?: Partial<EntityIdentifier>
}

export interface ObjectComboMappingFormRawValues {
  key?: string
  value?: string
  leftObjectId?: string
  leftObjectType?: string
  rightObjectId?: string
  rightObjectType?: string
}

export type ObjectComboMappingFormType = UseFormReturnType<ObjectComboMappingFormRawValues, (values: ObjectComboMappingFormRawValues) => ObjectComboMappingFormRawValues>

export const ObjectComboMappingForm = (props: ObjectComboMappingFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false,
  } = props;

  const {t} = useTranslation(["common"]);

  const form = useObjectComboMappingForm({
    defaultValues: defaultValues,
  });

  const onFormSubmit = useCallback(async (values: typeof form.values, event: React.FormEvent<HTMLFormElement>) => {
    if (onSubmit) {
      const action = (event.nativeEvent as any).submitter.value as string;
      const newAfter = action === "submit-and-new";

      onSubmit({
        key: values.key!,
        value: values.value!,
        leftObject: {
          id: values.leftObjectId!,
          type: values.leftObjectType!,
        },
        rightObject: {
          id: values.rightObjectId!,
          type: values.rightObjectType!,
        },
      }, newAfter);
    }
  }, [form, onSubmit]);

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <TextInput
        label={t("common.entity.objectcombomapping.form.field.key", "Key")}
        placeholder={t<string>("common.entity.objectcombomapping.form.field.key.placeholder", "Key")}
        styles={{wrapper: {width: "50%"}}}
        withAsterisk
        required
        {...form.getInputProps("key")} />
      <TextInput
        label={t("common.entity.objectcombomapping.form.field.value", "Value")}
        placeholder={t<string>("common.entity.objectcombomapping.form.field.value.placeholder", "Value")}
        styles={{wrapper: {width: "50%"}}}
        withAsterisk
        required
        {...form.getInputProps("value")} />
      <Divider my="sm" variant="dotted"/>
      <EntityTypeSelect
        label={t("common.entity.objectcombomapping.form.field.leftobject", "Left Object")}
        placeholder={t<string>("common.entity.objectcombomapping.form.field.leftobject.placeholder", "Left Object")}
        disabled={options?.leftObjectSelectDisabled}
        withAsterisk
        required
        clearable
        {...form.getInputProps("leftObjectType")} />
      <EntitySelect
        pt="md"
        disabled={options?.leftObjectSelectDisabled}
        entityType={form.getTransformedValues().leftObjectType}
        regionId={options?.regionId}
        withAsterisk
        required
        clearable
        {...form.getInputProps("leftObjectId")} />
      <Divider my="sm" variant="dotted"/>
      <EntityTypeSelect
        label={t("common.entity.objectcombomapping.form.field.rightobject", "Right Object")}
        placeholder={t<string>("common.entity.objectcombomapping.form.field.rightobject.placeholder", "Right Object")}
        disabled={options?.rightObjectSelectDisabled}
        withAsterisk
        required
        clearable
        {...form.getInputProps("rightObjectType")} />
      <EntitySelect
        pt="md"
        disabled={options?.rightObjectSelectDisabled}
        entityType={form.getTransformedValues().rightObjectType}
        regionId={options?.regionId}
        withAsterisk
        required
        clearable
        {...form.getInputProps("rightObjectId")} />
      <Flex gap="md" mt="xl">
        <Button type="submit" value="submit" loading={submitting} fullWidth>
          {t("common.action.submit", "Submit", {ns: "common"})}
        </Button>
        {options?.newAfter && (
          <Button type="submit" value="submit-and-new" loading={submitting} variant="outline" fullWidth>
            {t("common.action.submitandnew", "Submit and New", {ns: "common"})}
          </Button>
        )}
      </Flex>
    </form>
  );
};

interface ObjectComboMappingFormModalProps extends ObjectComboMappingFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const ObjectComboMappingFormModal = (props: ObjectComboMappingFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    submitting,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <ObjectComboMappingForm defaultValues={defaultValues} onSubmit={onSubmit} submitting={submitting}/>
    </Modal>
  );
};
